import React, {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  FaCamera,
  FaCheck,
  FaCopy,
  FaLightbulb,
  FaPen,
  FaSpinner,
} from "react-icons/fa";
import { toast } from "react-toastify";
import { useParams } from "react-router-dom";

import bannerSbf from "../../../assets/credenciamento2026/banner2026-credenciamento.png";
import ilustBaiana from "../../../assets/credenciamento2026/baiana-2026.png";
import footerCred2026 from "../../../assets/credenciamento2026/footer-2026.png";
import "./credenciamentoPatrocinador2026.css";
import {
  validatePatrocinadorInviteToken,
  submitCredenciamentoPatrocinador2026,
} from "./mockCredenciamentoPatrocinador2026";
import api from "../../../services/axios";

const MAX_BIO_FOTO_BYTES = 5 * 1024 * 1024;
function onlyDigits(s) {
  return String(s || "").replace(/\D/g, "");
}

function formatCpf(v) {
  const d = onlyDigits(v).slice(0, 11);
  if (d.length <= 3) return d;
  if (d.length <= 6) return `${d.slice(0, 3)}.${d.slice(3)}`;
  if (d.length <= 9) return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6)}`;
  return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}`;
}

function formatPhone(v) {
  const d = onlyDigits(v).slice(0, 11);
  if (d.length <= 2) return d.length ? `(${d}` : "";
  if (d.length <= 7) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
  return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
}

function isValidEmail(e) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(e || "").trim());
}

async function usuarioJaCadastradoPorEmail(email) {
  const trimmed = String(email || "").trim();
  if (!trimmed) return false;
  try {
    const resp = await api.post("/usuarios-verifica-email", { email: trimmed }, {
      skipAuth: true,
    });
    return Boolean(resp.data?.dados);
  } catch {
    return false;
  }
}

function initialsFromName(name) {
  const parts = String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
}

function isValidDateBr(s) {
  const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(String(s || "").trim());
  if (!m) return false;
  const day = Number(m[1]);
  const month = Number(m[2]);
  const year = Number(m[3]);
  if (month < 1 || month > 12 || day < 1 || day > 31) return false;
  const dt = new Date(year, month - 1, day);
  return (
    dt.getFullYear() === year &&
    dt.getMonth() === month - 1 &&
    dt.getDate() === day
  );
}

const initialForm = () => ({
  nomeCracha: "",
  email: "",
  cpf: "",
  dataNascimento: "",
  sexo: "",
  telComercial: "",
  celular: "",
  nomeInstituicao: "",
  cargo: "",
  acaoEspecial: "",
  usoImagem: false,
  bioCurta: "",
  aceiteRegulamento: false,
});

const STEPS_META = [
  { key: 0, label: "Identificação", Icon: FaLightbulb },
  { key: 1, label: "Bio", Icon: FaCamera },
  { key: 2, label: "Revisão", Icon: FaCheck },
];

const REVIEW_MODAL_TITLES = {
  cracha_email: "Editar crachá e e-mail",
  instituicao: "Editar instituição e cargo",
  bio: "Editar mini bio",
  foto: "Editar foto de perfil",
  uso_imagem: "Editar autorização de uso de imagem",
};

function CredenciamentoPatrocinador2026() {
  const { token: tokenParam } = useParams();
  const uid = useId();
  const [inviteStatus, setInviteStatus] = useState("loading");
  const [inviteErrorMessage, setInviteErrorMessage] = useState("");
  const [nomeInstituicaoFixaPorConvite, setNomeInstituicaoFixaPorConvite] =
    useState(false);
  const [step, setStep] = useState(0);
  const [submitted, setSubmitted] = useState(false);
  const [protocolo, setProtocolo] = useState("");
  const [conviteUrlResposta, setConviteUrlResposta] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [touched, setTouched] = useState({});
  const [form, setForm] = useState(initialForm);
  const [bioFoto, setBioFoto] = useState(null);
  const [bioFotoPreview, setBioFotoPreview] = useState("");
  const bioFileInputRef = useRef(null);
  const [reviewEdit, setReviewEdit] = useState(null);
  const [editDraft, setEditDraft] = useState(() => initialForm());
  const [modalBioFoto, setModalBioFoto] = useState(null);
  const [modalFotoPreview, setModalFotoPreview] = useState("");
  const modalFotoInputRef = useRef(null);
  const [emailLoginVerificando, setEmailLoginVerificando] = useState(false);
  const [emailLoginJaExiste, setEmailLoginJaExiste] = useState(false);
  const [emailModalVerificando, setEmailModalVerificando] = useState(false);
  const [emailModalJaExiste, setEmailModalJaExiste] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setInviteStatus("loading");
    setInviteErrorMessage("");
    setNomeInstituicaoFixaPorConvite(false);

    (async () => {
      const resultado = await validatePatrocinadorInviteToken(tokenParam);
      if (cancelled) return;
      if (resultado.ok) {
        const empresa = String(resultado.dados?.empresa ?? "").trim();
        if (!empresa) {
          setInviteStatus("invalid");
          setInviteErrorMessage(
            "O convite não retornou a instituição vinculada. Solicite um novo link ao organizador.",
          );
          return;
        }
        setNomeInstituicaoFixaPorConvite(true);
        setForm((prev) => ({
          ...prev,
          nomeInstituicao: empresa,
        }));
        setInviteStatus("valid");
      } else {
        setInviteStatus("invalid");
        setInviteErrorMessage(
          resultado.mensagem ||
            "Não foi possível validar este convite. Solicite um novo link ao organizador.",
        );
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [tokenParam]);

  useEffect(() => {
    if (!bioFoto) {
      setBioFotoPreview("");
      return undefined;
    }
    const url = URL.createObjectURL(bioFoto);
    setBioFotoPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [bioFoto]);

  const markTouched = useCallback((keys) => {
    setTouched((t) => {
      const n = { ...t };
      keys.forEach((k) => {
        n[k] = true;
      });
      return n;
    });
  }, []);

  const clearBioFoto = useCallback(() => {
    setBioFoto(null);
    if (bioFileInputRef.current) bioFileInputRef.current.value = "";
    markTouched(["bioFoto"]);
  }, [markTouched]);

  const handleBioFotoChange = useCallback((e) => {
    const input = e.target;
    const f = input.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) {
      toast.error("Envie apenas arquivo de imagem (JPG, PNG, WebP ou GIF).");
      input.value = "";
      markTouched(["bioFoto"]);
      return;
    }
    if (f.size > MAX_BIO_FOTO_BYTES) {
      toast.error("Imagem muito grande. Tamanho máximo: 5 MB.");
      input.value = "";
      markTouched(["bioFoto"]);
      return;
    }
    setBioFoto(f);
    markTouched(["bioFoto"]);
  }, [markTouched]);

  const update = useCallback((patch) => {
    setForm((f) => ({ ...f, ...patch }));
  }, []);

  const openReviewEdit = useCallback(
    (key) => {
      setEditDraft({ ...form });
      if (key === "foto") setModalBioFoto(bioFoto);
      if (key === "cracha_email") {
        setEmailModalJaExiste(false);
        setEmailModalVerificando(false);
      }
      setReviewEdit(key);
    },
    [form, bioFoto]
  );

  const closeReviewEdit = useCallback(() => {
    setReviewEdit(null);
  }, []);

  const saveReviewEdit = useCallback(async () => {
    if (reviewEdit === "foto") {
      setBioFoto(modalBioFoto);
      if (!modalBioFoto && bioFileInputRef.current) bioFileInputRef.current.value = "";
    } else if (reviewEdit === "cracha_email") {
      const em = String(editDraft.email || "").trim();
      if (!isValidEmail(em)) {
        toast.error("Informe um e-mail válido.");
        return;
      }
      setEmailModalVerificando(true);
      try {
        const existe = await usuarioJaCadastradoPorEmail(em);
        setEmailModalJaExiste(existe);
        if (existe) {
          toast.error("Este e-mail já possui cadastro.");
          return;
        }
      } finally {
        setEmailModalVerificando(false);
      }
      update({
        nomeCracha: editDraft.nomeCracha,
        email: editDraft.email,
      });
    } else if (reviewEdit === "instituicao" && nomeInstituicaoFixaPorConvite) {
      update({ cargo: editDraft.cargo });
    } else if (reviewEdit) {
      update(editDraft);
    }
    setReviewEdit(null);
  }, [
    reviewEdit,
    modalBioFoto,
    editDraft,
    update,
    nomeInstituicaoFixaPorConvite,
  ]);

  useEffect(() => {
    if (reviewEdit !== "foto") {
      setModalFotoPreview("");
      return undefined;
    }
    if (!modalBioFoto) {
      setModalFotoPreview("");
      return undefined;
    }
    const u = URL.createObjectURL(modalBioFoto);
    setModalFotoPreview(u);
    return () => URL.revokeObjectURL(u);
  }, [reviewEdit, modalBioFoto]);

  useEffect(() => {
    if (!reviewEdit) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setReviewEdit(null);
    };
    window.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [reviewEdit]);

  const handleModalBioFotoChange = useCallback((e) => {
    const input = e.target;
    const f = input.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) {
      toast.error("Envie apenas arquivo de imagem (JPG, PNG, WebP ou GIF).");
      input.value = "";
      return;
    }
    if (f.size > MAX_BIO_FOTO_BYTES) {
      toast.error("Imagem muito grande. Tamanho máximo: 5 MB.");
      input.value = "";
      return;
    }
    setModalBioFoto(f);
  }, []);

  useLayoutEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    document.getElementById("c26-main")?.scrollIntoView({
      block: "start",
      behavior: "auto",
    });
  }, [step, submitted]);

  const errorsStep0 = useMemo(() => {
    const e = {};
    const req = (k, cond, msg) => {
      if (cond) e[k] = msg;
    };
    req("nomeCracha", !form.nomeCracha.trim(), "Informe o nome para o crachá.");
    if (!isValidEmail(form.email)) {
      req("email", true, "E-mail inválido.");
    } else if (emailLoginJaExiste) {
      req("email", true, "Este e-mail já possui cadastro.");
    }
    const cpfD = onlyDigits(form.cpf);
    req("cpf", cpfD.length !== 11, "CPF deve ter 11 dígitos.");
    req(
      "dataNascimento",
      !isValidDateBr(form.dataNascimento),
      "Data de nascimento inválida (use DD/MM/AAAA)."
    );
    req("sexo", !form.sexo, "Selecione o sexo.");
    req(
      "telComercial",
      onlyDigits(form.telComercial).length < 10,
      "Telefone comercial inválido."
    );
    req("celular", onlyDigits(form.celular).length < 10, "Celular inválido.");
    req("nomeInstituicao", !form.nomeInstituicao.trim(), "Informe a instituição.");
    req("cargo", !form.cargo.trim(), "Informe o cargo.");
    return e;
  }, [form, emailLoginJaExiste]);

  const errorsStepBio = useMemo(() => {
    const e = {};
    if (!String(form.bioCurta || "").trim()) {
      e.bioCurta = "Informe sua mini bio ou mensagem para networking.";
    }
    if (!bioFoto) {
      e.bioFoto = "Envie uma foto para o perfil.";
    }
    if (!form.acaoEspecial) {
      e.acaoEspecial =
        "Selecione se tem interesse em participar da degustação de caviar.";
    }
    return e;
  }, [form, bioFoto]);

  const errorsStepReview = useMemo(() => {
    const e = {};
    if (!form.aceiteRegulamento) {
      e.aceiteRegulamento =
        "É necessário aceitar a declaração para concluir o credenciamento.";
    }
    return e;
  }, [form]);

  const handleNext = async () => {
    if (step === 0) {
      const step0Keys = [
        "nomeCracha",
        "email",
        "cpf",
        "dataNascimento",
        "sexo",
        "telComercial",
        "celular",
        "nomeInstituicao",
        "cargo",
      ];

      if (Object.keys(errorsStep0).length > 0) {
        markTouched(step0Keys);
        toast.error("Preencha ou corrija os campos obrigatórios antes de avançar.");
        return;
      }

      if (isValidEmail(form.email)) {
        setEmailLoginVerificando(true);
        try {
          const existe = await usuarioJaCadastradoPorEmail(form.email);
          setEmailLoginJaExiste(existe);
          markTouched(["email"]);
          if (existe) {
            toast.error("Este e-mail já possui cadastro.");
            return;
          }
        } finally {
          setEmailLoginVerificando(false);
        }
      }

      setStep(1);
      return;
    }

    if (step === 1) {
      const step1Keys = ["bioCurta", "bioFoto", "acaoEspecial"];
      if (Object.keys(errorsStepBio).length > 0) {
        markTouched(step1Keys);
        toast.error("Preencha ou corrija os campos obrigatórios antes de avançar.");
        return;
      }
      setStep(2);
    }
  };

  const handleBack = () => {
    if (step > 0) setStep(step - 1);
  };

  const handleSubmit = async () => {
    markTouched(["aceiteRegulamento"]);
    if (!form.aceiteRegulamento) {
      toast.error("Aceite a declaração para concluir o credenciamento.");
      return;
    }
    setSubmitting(true);
    try {
      const formEnvioApi = { ...form };
      delete formEnvioApi.nomeInstituicao;
      const res = await submitCredenciamentoPatrocinador2026({
        inviteToken: tokenParam,
        ...formEnvioApi,
        bioFoto,
      });
      if (!res.ok) {
        toast.error(res.mensagem || "Não foi possível concluir. Tente novamente.");
        return;
      }
      setProtocolo(res.protocolo || "");
      const u = String(res.url || "").trim();
      if (u && !/^https?:\/\//i.test(u) && typeof window !== "undefined") {
        setConviteUrlResposta(`${window.location.origin}${u.startsWith("/") ? u : `/${u}`}`);
      } else {
        setConviteUrlResposta(u);
      }
      setSubmitted(true);
      toast.success(res.mensagem);
    } catch {
      toast.error("Não foi possível concluir. Tente novamente.");
    } finally {
      setSubmitting(false);
    }
  };

  const showErr = (name, errMap) =>
    Boolean(touched[name] && errMap[name]);

  const stepper = (
    <ol className="c26-stepper" aria-label="Etapas do credenciamento">
      {STEPS_META.map((s, i) => {
        const active =
          inviteStatus === "valid" && !submitted && step === i;
        const done =
          inviteStatus === "valid" && (submitted || step > i);
        const upcoming =
          inviteStatus === "valid" && !submitted && i > step;
        return (
          <li
            key={s.key}
            className={`c26-step${active ? " is-active" : ""}${done ? " is-done" : ""}${upcoming ? " is-upcoming" : ""}`}
            aria-current={active ? "step" : undefined}
          >
            <span className="c26-step-icon" aria-hidden="true">
              <s.Icon />
            </span>
            <span className="c26-step-label">{s.label}</span>
          </li>
        );
      })}
    </ol>
  );

  const field = (name, label, hint, input, errMap) => {
    const hintId = hint ? `${uid}-${name}-hint` : undefined;
    const errId = showErr(name, errMap) ? `${uid}-${name}-err` : undefined;
    const describedBy = [hintId, errId].filter(Boolean).join(" ") || undefined;
    return (
      <div className={`c26-field ${name === "bioCurta" ? "c26-field-full" : ""}`}>
        <div className="c26-label-block">
          <label className="c26-label" htmlFor={`${uid}-${name}`}>
            {label} <span className="req">*</span>
          </label>
        </div>
        {React.cloneElement(input, {
          id: `${uid}-${name}`,
          "aria-invalid": showErr(name, errMap),
          "aria-describedby": describedBy,
        })}
        {hint ? (
          <p className="c26-hint" id={hintId}>
            {hint}
          </p>
        ) : null}
        {showErr(name, errMap) ? (
          <span className="c26-error" id={errId} role="alert">
            {errMap[name]}
          </span>
        ) : null}
      </div>
    );
  };

  const panelStep0 = (
    <div className="c26-step-panel">
      <p className="c28-lead">
        Caro Patrocinador, preencha seus dados para agilizar o seu credenciamento.<br/>
        Dúvidas entre em contato através do e-mail:
        <a href="mailto:debora.g@informa.com">debora.g@informa.com</a>.
      </p>
      <div className="c26-form-grid c26-two">
        {field(
          "nomeCracha",
          "Nome para o crachá",
          null,
          <input
            className="c26-input"
            value={form.nomeCracha}
            onChange={(e) => update({ nomeCracha: e.target.value })}
            onBlur={() => markTouched(["nomeCracha"])}
            autoComplete="name"
            required
          />,
          errorsStep0
        )}
        {field(
          "email",
          "E-mail (login)",
          <>
            Este será o seu e-mail de acesso.
            {emailLoginVerificando ? (
              <>
                {" "}
                <FaSpinner className="fa-spin" aria-hidden />
                <span className="visually-hidden"> Verificando e-mail…</span>
              </>
            ) : null}
          </>,
          <input
            type="email"
            className="c26-input"
            value={form.email}
            onChange={(e) => {
              setEmailLoginJaExiste(false);
              update({ email: e.target.value });
            }}
            onBlur={async (e) => {
              markTouched(["email"]);
              const v = e.target.value.trim();
              if (!isValidEmail(v)) {
                setEmailLoginJaExiste(false);
                return;
              }
              setEmailLoginVerificando(true);
              try {
                const existe = await usuarioJaCadastradoPorEmail(v);
                setEmailLoginJaExiste(existe);
              } finally {
                setEmailLoginVerificando(false);
              }
            }}
            autoComplete="email"
            required
          />,
          errorsStep0
        )}
        {field(
          "cpf",
          "CPF",
          null,
          <input
            className="c26-input"
            inputMode="numeric"
            value={form.cpf}
            onChange={(e) => update({ cpf: formatCpf(e.target.value) })}
            onBlur={() => markTouched(["cpf"])}
            required
          />,
          errorsStep0
        )}
        {field(
          "dataNascimento",
          "Data de nascimento",
          "Formato DD/MM/AAAA.",
          <input
            className="c26-input"
            placeholder="DD/MM/AAAA"
            value={form.dataNascimento}
            onChange={(e) => {
              const raw = onlyDigits(e.target.value).slice(0, 8);
              let out = raw;
              if (raw.length > 2) out = `${raw.slice(0, 2)}/${raw.slice(2)}`;
              if (raw.length > 4) out = `${raw.slice(0, 2)}/${raw.slice(2, 4)}/${raw.slice(4)}`;
              update({ dataNascimento: out });
            }}
            onBlur={() => markTouched(["dataNascimento"])}
            required
          />,
          errorsStep0
        )}
        <div className="c26-field c26-field-full">
          <span className="c26-label">
            Sexo <span className="req">*</span>
          </span>
          <div className="c26-radio-row" role="group" aria-label="Sexo">
            {["Masculino", "Feminino"].map((opt) => (
              <label key={opt} className="c26-radio">
                <input
                  type="radio"
                  name={`${uid}-sexo`}
                  checked={form.sexo === opt}
                  onChange={() => {
                    update({ sexo: opt });
                    markTouched(["sexo"]);
                  }}
                />
                {opt}
              </label>
            ))}
          </div>
          {showErr("sexo", errorsStep0) ? (
            <span className="c26-error" role="alert">
              {errorsStep0.sexo}
            </span>
          ) : null}
        </div>
        {field(
          "telComercial",
          "Telefone comercial",
          null,
          <input
            className="c26-input"
            inputMode="tel"
            value={form.telComercial}
            onChange={(e) => update({ telComercial: formatPhone(e.target.value) })}
            onBlur={() => markTouched(["telComercial"])}
            autoComplete="tel"
            required
          />,
          errorsStep0
        )}
        {field(
          "celular",
          "Celular",
          "Usado para comunicações no dia do evento; informe um número que estará com você.",
          <input
            className="c26-input"
            inputMode="tel"
            value={form.celular}
            onChange={(e) => update({ celular: formatPhone(e.target.value) })}
            onBlur={() => markTouched(["celular"])}
            autoComplete="tel-national"
            required
          />,
          errorsStep0
        )}
        {field(
          "nomeInstituicao",
          "Nome da instituição",
          nomeInstituicaoFixaPorConvite
            ? ""
            : null,
          <input
            className="c26-input"
            value={form.nomeInstituicao}
            onChange={(e) => update({ nomeInstituicao: e.target.value })}
            onBlur={() => markTouched(["nomeInstituicao"])}
            disabled={nomeInstituicaoFixaPorConvite}
            required={!nomeInstituicaoFixaPorConvite}
            autoComplete="organization"
          />,
          errorsStep0
        )}
        {field(
          "cargo",
          "Cargo",
          null,
          <input
            className="c26-input"
            value={form.cargo}
            onChange={(e) => update({ cargo: e.target.value })}
            onBlur={() => markTouched(["cargo"])}
            required
          />,
          errorsStep0
        )}
      </div>
    </div>
  );

  const panelStepBio = (
    <div className="c26-step-panel">
      <p className="c26-lead">
        Insira aqui uma mini-bio e adicione uma foto para o seu perfil.
      </p>
      <div className="c26-form-grid">
        <div className="c26-field c26-field-full">
          <div className="c26-label-block">
            <label className="c26-label" htmlFor={`${uid}-bio`}>
              Mini bio ou mensagem para networking <span className="req">*</span>
            </label>
          </div>
          <textarea
            id={`${uid}-bio`}
            className="c26-textarea"
            rows={4}
            value={form.bioCurta}
            onChange={(e) => update({ bioCurta: e.target.value })}
            onBlur={() => markTouched(["bioCurta"])}
            placeholder="Uma ou duas linhas sobre você, interesses ou o que busca no evento."
            aria-invalid={showErr("bioCurta", errorsStepBio)}
            aria-describedby={
              showErr("bioCurta", errorsStepBio) ? `${uid}-bio-err` : undefined
            }
            required
          />
          {showErr("bioCurta", errorsStepBio) ? (
            <span className="c26-error" id={`${uid}-bio-err`} role="alert">
              {errorsStepBio.bioCurta}
            </span>
          ) : null}
        </div>
        <div className="c26-field c26-field-full">
          <div className="c26-label-block">
            <span className="c26-label" id={`${uid}-bio-foto-label`}>
              Foto para perfil <span className="req">*</span>
            </span>
          </div>
          <div
            className="c26-upload"
            role="group"
            aria-labelledby={`${uid}-bio-foto-label`}
            aria-invalid={showErr("bioFoto", errorsStepBio)}
          >
            <input
              ref={bioFileInputRef}
              type="file"
              id={`${uid}-bio-foto`}
              className="c26-upload-input"
              accept="image/jpeg,image/png,image/webp,image/gif"
              onChange={handleBioFotoChange}
              aria-describedby={
                showErr("bioFoto", errorsStepBio) ? `${uid}-bio-foto-err` : undefined
              }
            />
            {bioFotoPreview ? (
              <>
                <img
                  src={bioFotoPreview}
                  alt="Pré-visualização da foto selecionada"
                  className="c26-upload-preview"
                />
                <div className="c26-upload-actions">
                  <button
                    type="button"
                    className="c26-btn c26-btn-ghost c26-btn-small"
                    onClick={() => bioFileInputRef.current?.click()}
                  >
                    Trocar imagem
                  </button>
                  <button
                    type="button"
                    className="c26-btn c26-btn-ghost c26-btn-small"
                    onClick={clearBioFoto}
                  >
                    Remover
                  </button>
                </div>
              </>
            ) : (
              <label htmlFor={`${uid}-bio-foto`} className="c26-upload-zone">
                <strong>Toque ou clique para escolher uma imagem</strong>
                <p>Formatos: JPG, PNG, WebP ou GIF · tamanho máximo 5 MB</p>
              </label>
            )}
          </div>
          {showErr("bioFoto", errorsStepBio) ? (
            <span className="c26-error" id={`${uid}-bio-foto-err`} role="alert">
              {errorsStepBio.bioFoto}
            </span>
          ) : null}
        </div>
        <div className="c26-field c26-field-full c26-field-separated">
          <span className="c26-label" id={`${uid}-acao-especial-label`}>
            Durante a edição deste ano teremos uma ação de degustação de Caviar, você tem
            interesse em participar? <span className="req">*</span>
          </span>
          <div
            className="c26-radio-row"
            role="radiogroup"
            aria-labelledby={`${uid}-acao-especial-label`}
          >
            {[
              ["sim", "Sim"],
              ["nao", "Não"],
            ].map(([value, label]) => (
              <label key={value} className="c26-radio">
                <input
                  type="radio"
                  name={`${uid}-acao-especial`}
                  checked={form.acaoEspecial === value}
                  onChange={() => {
                    update({ acaoEspecial: value });
                    markTouched(["acaoEspecial"]);
                  }}
                />
                {label}
              </label>
            ))}
          </div>
          {showErr("acaoEspecial", errorsStepBio) ? (
            <span className="c26-error" role="alert">
              {errorsStepBio.acaoEspecial}
            </span>
          ) : null}
        </div>
        <div className="c26-field c26-field-full">
          <div className="c26-termo">
            <p>
              As <strong>fotografias e imagens do evento</strong> poderão ser usadas pela{" "}
              <strong>Informa Markets</strong> para promover o setor e o evento. Você
              autoriza a utilização de sua <strong>imagem e/ou voz</strong> em materiais
              impressos ou virtuais, no Brasil e no exterior.
            </p>
          </div>
          <label className="c26-check" style={{ marginTop: "0.75rem" }}>
            <input
              type="checkbox"
              checked={form.usoImagem}
              onChange={(e) => update({ usoImagem: e.target.checked })}
            />
            <span>
              <strong>Concordo com a autorização de uso de imagem</strong> (opcional)
            </span>
          </label>
        </div>
      </div>
    </div>
  );

  const reviewPencil = (key, ariaLabel) => (
    <button
      type="button"
      className="c26-review-edit-btn"
      onClick={() => openReviewEdit(key)}
      aria-label={ariaLabel}
    >
      <FaPen aria-hidden />
    </button>
  );

  const reviewList = (
    <dl className="c26-review">
      <dt>Crachá / e-mail</dt>
      <dd>
        <div className="c26-review-dd-wrap">
          <span className="c26-review-value">
            {form.nomeCracha} — {form.email}
          </span>
          {reviewPencil("cracha_email", "Editar crachá e e-mail")}
        </div>
      </dd>
      <dt>Instituição</dt>
      <dd>
        <div className="c26-review-dd-wrap">
          <span className="c26-review-value">
            {form.nomeInstituicao} — {form.cargo}
          </span>
          {reviewPencil("instituicao", "Editar instituição e cargo")}
        </div>
      </dd>
      <dt>Mini bio</dt>
      <dd>
        <div className="c26-review-dd-wrap">
          <span className="c26-review-value">
            {form.bioCurta.trim() ? form.bioCurta : "—"}
          </span>
          {reviewPencil("bio", "Editar mini bio")}
        </div>
      </dd>
      <dt>Foto de perfil</dt>
      <dd>
        <div className="c26-review-dd-wrap">
          <span className="c26-review-value">
            {bioFoto
              ? `${bioFoto.name} (${Math.round(bioFoto.size / 1024)} KB)`
              : "Nenhuma imagem enviada"}
          </span>
          {reviewPencil("foto", "Editar foto de perfil")}
        </div>
      </dd>
      <dt>Degustação de caviar</dt>
      <dd>
        <span className="c26-review-value">
          {form.acaoEspecial === "sim"
            ? "Sim"
            : form.acaoEspecial === "nao"
              ? "Não"
              : "—"}
        </span>
      </dd>
      <dt>Uso de imagem no evento</dt>
      <dd>
        <div className="c26-review-dd-wrap">
          <span className="c26-review-value">
            {form.usoImagem ? "Autorizado" : "Não autorizado"}
          </span>
          {reviewPencil("uso_imagem", "Editar autorização de uso de imagem")}
        </div>
      </dd>
    </dl>
  );

  const credentialPreview = (
    <article
      className="c26-credential"
      aria-label="Pré-visualização da credencial do evento"
    >
      <header className="c26-credential-header">
        <div className="c26-credential-header-top">
          <span className="c26-credential-brand">SBF</span>
          <span className="c26-credential-year">2026</span>
        </div>
        <p className="c26-credential-tagline">Saúde Business Fórum</p>
      </header>
      <div className="c26-credential-body">
        <div className="c26-credential-photo">
          {bioFotoPreview ? (
            <img
              src={bioFotoPreview}
              alt=""
              className="c26-credential-photo-img"
            />
          ) : (
            <div
              className="c26-credential-photo-placeholder"
              aria-hidden="true"
            >
              {initialsFromName(form.nomeCracha)}
            </div>
          )}
        </div>
        <p className="c26-credential-name">
          {form.nomeCracha.trim() || "Nome no crachá"}
        </p>
        <p className="c26-credential-job">
          {form.cargo.trim() || "Cargo"}
        </p>
        <p className="c26-credential-institution">
          {form.nomeInstituicao.trim() || "Instituição"}
        </p>
        <p className="c26-credential-email">{form.email.trim() || "—"}</p>
      </div>
      <footer className="c26-credential-footer c26-credential-footer--patrocinador">
        <span className="c26-credential-badge-type c26-credential-badge-type--empresa-convite">
          {form.nomeInstituicao.trim() || "Instituição"}
        </span>
        <span className="c26-credential-venue">Costa do Sauípe · Bahia</span>
      </footer>
    </article>
  );

  const panelStep3 = (
    <div className="c26-step-panel">
      <p className="c26-lead">
        Confira os dados antes de enviar.
      </p>
      <div className="c26-review-grid">
        <div className="c26-credential-column">
          <h2 className="c26-credential-preview-title">Credencial do evento</h2>
          {credentialPreview}
        </div>
        <div className="c26-review-summary">
          <h2 className="c26-review-heading">Resumo dos dados</h2>
          {reviewList}
        </div>
      </div>
      <div className="c26-field c26-field-full" style={{ marginTop: "1.25rem" }}>
        <label className="c26-check">
          <input
            type="checkbox"
            checked={form.aceiteRegulamento}
            onChange={(e) => {
              update({ aceiteRegulamento: e.target.checked });
              markTouched(["aceiteRegulamento"]);
            }}
            aria-invalid={showErr("aceiteRegulamento", errorsStepReview)}
          />
          <span>
            Declaro que as informações são verdadeiras e aceito seguir o regulamento do
            evento. <span className="req">*</span>
          </span>
        </label>
        {showErr("aceiteRegulamento", errorsStepReview) ? (
          <span className="c26-error" role="alert">
            {errorsStepReview.aceiteRegulamento}
          </span>
        ) : null}
      </div>
    </div>
  );

  const copiarConviteResposta = async (texto) => {
    const t = String(texto || "").trim();
    if (!t) return;
    try {
      await navigator.clipboard.writeText(t);
      toast.success("Copiado para a área de transferência.");
    } catch {
      toast.error("Não foi possível copiar. Selecione o texto manualmente.");
    }
  };

  const successBlock = (
    <div className="c26-success">
      <div className="c26-success-icon" aria-hidden="true">
        <FaCheck />
      </div>
      <h2 className="c26-title" style={{ marginBottom: "0.5rem" }}>
        Credenciamento recebido
      </h2>
      <p className="c26-lead" style={{ margin: "0 auto 1rem" }}>
      Obrigado por concluir o credenciamento de patrocínio ao Saúde Business Fórum 2026. Você receberá um e-mail com mais informações.
      </p>
      {protocolo ? (
        <p className="c26-lead small" style={{ margin: "0 auto 0.75rem" }}>
          Protocolo: <strong className="font-monospace">{protocolo}</strong>
        </p>
      ) : null}
      {conviteUrlResposta ? (
        <div
          className="c26-field c26-field-full"
          style={{ maxWidth: "36rem", margin: "0 auto 1rem", textAlign: "left" }}
        >
          <p className="c26-hint" style={{ marginBottom: "0.5rem" }}>
            O mesmo convite pode ser usado por outras pessoas até expirar. Copie o link para compartilhar.
          </p>
          <div className="c26-field">
            <span className="c26-label">Link do convite</span>
            <div style={{ display: "flex", gap: "0.5rem", alignItems: "stretch", flexWrap: "wrap" }}>
              <input
                type="text"
                className="c26-input"
                readOnly
                value={conviteUrlResposta}
                style={{ flex: "1 1 12rem", fontSize: "0.85rem" }}
              />
              <button
                type="button"
                className="c26-btn c26-btn-ghost"
                onClick={() => copiarConviteResposta(conviteUrlResposta)}
                title="Copiar link"
              >
                <FaCopy aria-hidden /> Copiar
              </button>
            </div>
          </div>
        </div>
      ) : null}
      <div className="c26-success-footer-banner" aria-hidden="true">
        <img
          src={footerCred2026}
          alt=""
          className="c26-success-footer-banner__img"
          decoding="async"
        />
      </div>
      {/* <div className="c26-actions" style={{ justifyContent: "center", border: "none" }}>
        <Link to="/login" className="c26-btn c26-btn-primary" style={{ textDecoration: "none" }}>
          Ir para o login
        </Link>
      </div> */}
    </div>
  );

  const reviewEditModal =
    reviewEdit != null ? (
      <div
        className="c26-modal-overlay"
        role="presentation"
        onClick={closeReviewEdit}
      >
        <div
          className="c26-modal-sheet"
          role="dialog"
          aria-modal="true"
          aria-labelledby={`${uid}-review-modal-title`}
          onClick={(e) => e.stopPropagation()}
        >
          <h2 id={`${uid}-review-modal-title`} className="c26-modal-title">
            {REVIEW_MODAL_TITLES[reviewEdit] ?? "Editar"}
          </h2>
          <div className="c26-modal-body">
            {reviewEdit === "cracha_email" ? (
              <div className="c26-form-grid">
                <div className="c26-field c26-field-full">
                  <label className="c26-label" htmlFor={`${uid}-ed-nomeCracha`}>
                    Nome no crachá
                  </label>
                  <input
                    id={`${uid}-ed-nomeCracha`}
                    className="c26-input"
                    value={editDraft.nomeCracha}
                    onChange={(e) =>
                      setEditDraft((d) => ({ ...d, nomeCracha: e.target.value }))
                    }
                  />
                </div>
                <div className="c26-field c26-field-full">
                  <label className="c26-label" htmlFor={`${uid}-ed-email`}>
                    E-mail
                    {emailModalVerificando ? (
                      <>
                        {" "}
                        <FaSpinner className="fa-spin" aria-hidden />
                        <span className="visually-hidden"> Verificando…</span>
                      </>
                    ) : null}
                  </label>
                  <input
                    id={`${uid}-ed-email`}
                    type="email"
                    className="c26-input"
                    value={editDraft.email}
                    aria-invalid={emailModalJaExiste}
                    onChange={(e) => {
                      setEmailModalJaExiste(false);
                      setEditDraft((d) => ({ ...d, email: e.target.value }));
                    }}
                    onBlur={async (e) => {
                      const v = e.target.value.trim();
                      if (!isValidEmail(v)) {
                        setEmailModalJaExiste(false);
                        return;
                      }
                      setEmailModalVerificando(true);
                      try {
                        const existe = await usuarioJaCadastradoPorEmail(v);
                        setEmailModalJaExiste(existe);
                      } finally {
                        setEmailModalVerificando(false);
                      }
                    }}
                  />
                  {emailModalJaExiste ? (
                    <span className="c26-error" role="alert">
                      Este e-mail já possui cadastro.
                    </span>
                  ) : null}
                </div>
              </div>
            ) : null}
            {reviewEdit === "instituicao" ? (
              <div className="c26-form-grid">
                <div className="c26-field c26-field-full">
                  <label className="c26-label" htmlFor={`${uid}-ed-inst`}>
                    Instituição{" "}
                    {nomeInstituicaoFixaPorConvite ? (
                      <span className="c26-hint-inline">(convite)</span>
                    ) : null}
                  </label>
                  <input
                    id={`${uid}-ed-inst`}
                    className="c26-input"
                    value={editDraft.nomeInstituicao}
                    disabled={nomeInstituicaoFixaPorConvite}
                    readOnly={nomeInstituicaoFixaPorConvite}
                    aria-readonly={nomeInstituicaoFixaPorConvite || undefined}
                    onChange={(e) =>
                      setEditDraft((d) => ({
                        ...d,
                        nomeInstituicao: e.target.value,
                      }))
                    }
                  />
                </div>
                <div className="c26-field c26-field-full">
                  <label className="c26-label" htmlFor={`${uid}-ed-cargo`}>
                    Cargo
                  </label>
                  <input
                    id={`${uid}-ed-cargo`}
                    className="c26-input"
                    value={editDraft.cargo}
                    onChange={(e) =>
                      setEditDraft((d) => ({ ...d, cargo: e.target.value }))
                    }
                  />
                </div>
              </div>
            ) : null}
            {reviewEdit === "bio" ? (
              <div className="c26-field c26-field-full">
                <label className="c26-label" htmlFor={`${uid}-ed-bio`}>
                  Mini bio
                </label>
                <textarea
                  id={`${uid}-ed-bio`}
                  className="c26-textarea"
                  rows={4}
                  value={editDraft.bioCurta}
                  onChange={(e) =>
                    setEditDraft((d) => ({ ...d, bioCurta: e.target.value }))
                  }
                />
              </div>
            ) : null}
            {reviewEdit === "foto" ? (
              <div className="c26-upload">
                <input
                  ref={modalFotoInputRef}
                  type="file"
                  id={`${uid}-ed-foto`}
                  className="c26-upload-input"
                  accept="image/jpeg,image/png,image/webp,image/gif"
                  onChange={handleModalBioFotoChange}
                />
                {modalFotoPreview ? (
                  <>
                    <img
                      src={modalFotoPreview}
                      alt=""
                      className="c26-upload-preview"
                    />
                    <div className="c26-upload-actions">
                      <button
                        type="button"
                        className="c26-btn c26-btn-ghost c26-btn-small"
                        onClick={() => modalFotoInputRef.current?.click()}
                      >
                        Trocar imagem
                      </button>
                      <button
                        type="button"
                        className="c26-btn c26-btn-ghost c26-btn-small"
                        onClick={() => {
                          setModalBioFoto(null);
                          if (modalFotoInputRef.current)
                            modalFotoInputRef.current.value = "";
                        }}
                      >
                        Remover
                      </button>
                    </div>
                  </>
                ) : (
                  <label htmlFor={`${uid}-ed-foto`} className="c26-upload-zone">
                    <strong>Escolher imagem</strong>
                    <p>JPG, PNG, WebP ou GIF · máx. 5 MB</p>
                  </label>
                )}
              </div>
            ) : null}
            {reviewEdit === "uso_imagem" ? (
              <div className="c26-form-grid">
                <p className="c26-modal-termo-hint">
                  Imagens do evento podem ser usadas pela Informa Markets para divulgação,
                  conforme o regulamento do SBF 2026.
                </p>
                <label className="c26-check" style={{ marginTop: 0 }}>
                  <input
                    type="checkbox"
                    checked={editDraft.usoImagem}
                    onChange={(e) =>
                      setEditDraft((d) => ({ ...d, usoImagem: e.target.checked }))
                    }
                  />
                  <span>
                    <strong>Concordo com a autorização de uso de imagem</strong>
                  </span>
                </label>
              </div>
            ) : null}
          </div>
          <div className="c26-modal-actions">
            <button
              type="button"
              className="c26-btn c26-btn-ghost"
              onClick={closeReviewEdit}
            >
              Cancelar
            </button>
            <button
              type="button"
              className="c26-btn c26-btn-primary"
              onClick={saveReviewEdit}
              disabled={emailModalVerificando}
            >
              Salvar
            </button>
          </div>
        </div>
      </div>
    ) : null;

  const showInviteFormSteps = inviteStatus === "valid" && !submitted;
  const layoutSuccess =
    submitted || inviteStatus === "loading" || inviteStatus === "invalid";

  return (
    <div className="c26-page">
      <a href="#c26-main" className="c26-skip-link">
        Ir para o formulário
      </a>
      <div className="c26-banner-wrap">
        <img
          src={bannerSbf}
          alt="Saúde Business Fórum 2026 — Costa do Sauípe, Bahia"
          className="c26-banner"
          width={1100}
          height={220}
          decoding="async"
        />
      </div>
      <div
        className={`c26-layout${layoutSuccess ? " c26-layout--success" : ""}`}
      >
        {showInviteFormSteps ? (
          <aside className="c26-aside" aria-label="Identidade visual">
            <p className="c26-aside-badge">
              Patrocínador · Credenciamento 2026 · Costa do Sauípe
            </p>
            <figure className="c26-aside-figure">
              <img src={ilustBaiana} alt="" decoding="async" />
              <figcaption className="visually-hidden">
                Ilustração decorativa de boas-vindas
              </figcaption>
            </figure>
          </aside>
        ) : null}
        <main
          className="c26-main"
          id="c26-main"
          aria-busy={inviteStatus === "loading"}
        >
          {inviteStatus === "loading" ? (
            <div className="c26-invite-status c26-invite-status--loading">
              <FaSpinner
                className="fa-spin c26-invite-spinner"
                aria-hidden="true"
              />
              <p className="c26-lead">Validando o convite…</p>
              <p className="c26-help">
                Aguarde enquanto conferimos seu token com o servidor (simulação).
              </p>
            </div>
          ) : null}
          {inviteStatus === "invalid" ? (
            <div className="c26-invite-status" role="alert">
              <p className="c26-eyebrow">Acesso ao formulário</p>
              <h1 className="c26-title">Não foi possível abrir o credenciamento</h1>
              <p className="c26-lead">{inviteErrorMessage}</p>
              <p className="c26-help">
                Em caso de dúvida, fale com o time do evento:{" "}
                <a href="mailto:sbf@informa.com">sbf@informa.com</a>
              </p>
            </div>
          ) : null}
          {showInviteFormSteps ? (
            <>
              <p className="c26-eyebrow">Patrocinador</p>
              <h1 className="c26-title">
                Credenciamento de patrocinador · SBF 2026
              </h1>
              {stepper}
              {step === 0 ? panelStep0 : null}
              {step === 1 ? panelStepBio : null}
              {step === 2 ? panelStep3 : null}
              <div
                className={`c26-actions${step === 0 ? " c26-actions--first-step" : ""}`}
              >
                {step > 0 ? (
                  <button
                    type="button"
                    className="c26-btn c26-btn-ghost"
                    onClick={handleBack}
                    disabled={submitting}
                  >
                    Voltar
                  </button>
                ) : null}
                {step < 2 ? (
                  <button
                    type="button"
                    className="c26-btn c26-btn-primary"
                    onClick={handleNext}
                    disabled={submitting || emailLoginVerificando}
                  >
                    Avançar
                  </button>
                ) : (
                  <button
                    type="button"
                    className="c26-btn c26-btn-submit"
                    onClick={handleSubmit}
                    disabled={submitting || !form.aceiteRegulamento}
                    title={
                      !form.aceiteRegulamento
                        ? "Aceite a declaração para concluir"
                        : undefined
                    }
                  >
                    {submitting ? (
                      <>
                        <FaSpinner className="fa-spin" aria-hidden />
                        Enviando…
                      </>
                    ) : (
                      "Concluir credenciamento"
                    )}
                  </button>
                )}
              </div>
            </>
          ) : null}
          {inviteStatus === "valid" && submitted ? successBlock : null}
        </main>
      </div>
      {reviewEditModal}
    </div>
  );
}

export default CredenciamentoPatrocinador2026;
